import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useContext } from 'react';
import { WebSocketContext } from '../services/webSocket/webSocketContext';

/**
 * Custom hook for subscribing to device readings via WebSocket
 * @param {number[]} accountIds - Array of account IDs to subscribe to
 * @returns {Object} Object containing readings, connection status, and subscription functions
 */
export const useDeviceReadingsWebSocket = (accountIds = []) => {
  const { ws, wsConnected } = useContext(WebSocketContext);
  const [readings, setReadings] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState(null);
  const messageHandlerRef = useRef(null);
  const accountIdsRef = useRef(accountIds);
  const subscribeRef = useRef(null);
  
  // Memoize accountIds string to avoid unnecessary effect re-runs
  // Convert array to string for stable comparison
  const accountIdsStr = useMemo(() => accountIds.join(','), [accountIds]);
  const previousAccountIdsRef = useRef(accountIdsStr);

  // Update accountIds ref when it changes
  useEffect(() => {
    accountIdsRef.current = accountIds;
  }, [accountIds]);

  /**
   * Subscribe to device readings for the specified account IDs
   */
  const subscribe = useCallback(() => {
    if (!ws || !wsConnected || accountIds.length === 0) {
      return;
    }

    try {
      const subscribeMessage = {
        type: 'subscribeDeviceReadings',
        accountIds: accountIds.join(',')
      };
      ws.send(JSON.stringify(subscribeMessage));
      setIsSubscribed(true);
    } catch (error) {
      console.error('Error subscribing to device readings:', error);
    }
  }, [ws, wsConnected, accountIds]);

  /**
   * Unsubscribe from device readings
   * @param {string} accountIdsToUnsubscribe - Optional comma-separated account IDs to unsubscribe from. If omitted, unsubscribes from all.
   */
  const unsubscribe = useCallback((accountIdsToUnsubscribe = null) => {
    if (!ws || !wsConnected) {
      return;
    }

    try {
      const unsubscribeMessage = {
        type: 'unsubscribeDeviceReadings'
      };
      
      if (accountIdsToUnsubscribe) {
        unsubscribeMessage.accountIds = accountIdsToUnsubscribe;
      }
      
      ws.send(JSON.stringify(unsubscribeMessage));
      setIsSubscribed(false);
    } catch (error) {
      console.error('Error unsubscribing from device readings:', error);
    }
  }, [ws, wsConnected]);

  // Update subscribe ref when subscribe function changes
  useEffect(() => {
    subscribeRef.current = subscribe;
  }, [subscribe]);

  // Set up message handler for device readings
  useEffect(() => {
    if (!ws || !wsConnected) {
      return;
    }

    messageHandlerRef.current = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'connectionSuccess':
            // Auto-subscribe when connection is established
            if (accountIdsRef.current.length > 0) {
              subscribeRef.current();
            }
            break;
            
          case 'deviceReadingsSubscribed':
            setIsSubscribed(true);
            break;
            
          case 'deviceReadingsUpdate':
            // Distinguish between initial subscription data and new broadcasts
            if (data.message === 'New device readings available') {
              // NEW BROADCAST - Update UI immediately
              const timestamp = Date.now();
              setLastUpdateTimestamp(timestamp); // Force React to detect change
              if (data.data && Array.isArray(data.data)) {
                setReadings([...data.data]); // Create new array reference
              } else if (data.data) {
                // Handle single reading object
                setReadings([data.data]);
              }
            } else if (data.message === 'Device readings retrieved successfully') {
              // Initial subscription data - also update (optional, for initial state)
              if (data.data && Array.isArray(data.data)) {
                setReadings([...data.data]); // Create new array reference
              } else if (data.data) {
                // Handle single reading object
                setReadings([data.data]);
              }
            } else {
              // Fallback: update if message field is missing (backward compatibility)
              if (data.data && Array.isArray(data.data)) {
                setReadings([...data.data]); // Create new array reference
              } else if (data.data) {
                setReadings([data.data]);
              }
            }
            break;
            
          case 'deviceReadingsSubscriptionError':
            console.error('Device readings subscription error:', data.message);
            setIsSubscribed(false);
            break;
            
          case 'deviceReadingsUnsubscribed':
            setIsSubscribed(false);
            break;
            
          default:
            // Ignore other message types
            break;
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.addEventListener('message', messageHandlerRef.current);

    return () => {
      if (ws && messageHandlerRef.current) {
        ws.removeEventListener('message', messageHandlerRef.current);
      }
    };
  }, [ws, wsConnected, subscribe]);

  // Auto-subscribe when WebSocket connects and accountIds are available
  useEffect(() => {
    if (wsConnected && accountIds.length > 0 && !isSubscribed) {
      // Small delay to ensure connection is fully established
      const timeoutId = setTimeout(() => {
        subscribe();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [wsConnected, accountIdsStr, isSubscribed, subscribe]);

  // Resubscribe when accountIds change (unsubscribe from old, subscribe to new)
  useEffect(() => {
    const previousAccountIdsStr = previousAccountIdsRef.current;
    
    if (wsConnected && isSubscribed && accountIds.length > 0 && accountIdsStr !== previousAccountIdsStr) {
      // Unsubscribe from previous accountIds and subscribe to new ones
      const timeoutId = setTimeout(() => {
        unsubscribe();
        setTimeout(() => {
          subscribe();
        }, 50);
      }, 100);
      previousAccountIdsRef.current = accountIdsStr;
      return () => clearTimeout(timeoutId);
    } else if (accountIdsStr !== previousAccountIdsStr) {
      previousAccountIdsRef.current = accountIdsStr;
    }
  }, [wsConnected, isSubscribed, accountIdsStr, subscribe, unsubscribe]);

  // Unsubscribe when component unmounts
  useEffect(() => {
    return () => {
      if (ws?.current && wsConnected) {
        unsubscribe();
      }
    };
  }, [ws, wsConnected, unsubscribe]);

  return {
    readings,
    isConnected: wsConnected,
    isSubscribed,
    subscribe,
    unsubscribe,
    lastUpdateTimestamp // Expose timestamp to help components detect updates
  };
};
